import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Calendar = ({ selectedDate, onDateSelect, appointments = [], onMonthChange }) => {
  const [currentDate, setCurrentDate] = useState(selectedDate || new Date());
  const [displayedWeeks, setDisplayedWeeks] = useState([]);
  const lastMonthRef = useRef(null);
  const lastRangeRef = useRef(null);

  // Função para gerar as três semanas a partir da data atual
  const generateThreeWeeks = (date) => {
    const weeks = [];
    const startOfWeek = new Date(date);
    
    // Ajustar para o início da semana (domingo)
    startOfWeek.setDate(date.getDate() - date.getDay());
    
    // Gerar 3 semanas
    for (let week = 0; week < 3; week++) {
      const weekDays = [];
      for (let day = 0; day < 7; day++) {
        const dayDate = new Date(startOfWeek);
        dayDate.setDate(startOfWeek.getDate() + (week * 7) + day);
        weekDays.push(dayDate);
      }
      weeks.push(weekDays);
    }
    
    return weeks;
  };

  // Atualizar semanas quando a data muda
  useEffect(() => {
    const weeks = generateThreeWeeks(currentDate);
    setDisplayedWeeks(weeks);
    
    // Calcular o range de datas das semanas visíveis
    if (weeks.length > 0 && onMonthChange) {
      const firstDate = weeks[0][0];
      const lastDate = weeks[weeks.length - 1][6];
      
      // Usar a primeira e última data das semanas visíveis
      const startDate = new Date(firstDate);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(lastDate);
      endDate.setHours(23, 59, 59, 999);
      
      // Verificar se o range mudou antes de chamar onMonthChange
      const rangeKey = `${startDate.getTime()}-${endDate.getTime()}`;
      const isFirstCall = lastRangeRef.current === null;
      
      if (isFirstCall || lastRangeRef.current !== rangeKey) {
        lastRangeRef.current = rangeKey;
        // Chamar imediatamente (sem setTimeout) para garantir que seja executado
        onMonthChange(startDate, endDate);
      }
    }
    
    // Atualizar referência do mês para detectar mudanças significativas
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    lastMonthRef.current = { month: currentMonth, year: currentYear };
  }, [currentDate, onMonthChange]);

  // Navegar para a semana anterior
  const goToPreviousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  // Navegar para a próxima semana
  const goToNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  // Verificar se uma data tem agendamentos
  const hasAppointments = (date) => {
    if (!appointments || appointments.length === 0) return false;
    
    return appointments.some(apt => {
      if (!apt.dataAgendamento) return false;
      
      let aptDate;
      if (apt.dataAgendamento instanceof Date) {
        aptDate = apt.dataAgendamento;
      } else if (apt.dataAgendamento.toDate) {
        aptDate = apt.dataAgendamento.toDate();
      } else {
        aptDate = new Date(apt.dataAgendamento);
      }
      
      // Comparar apenas dia, mês e ano (ignorar hora)
      return aptDate.getFullYear() === date.getFullYear() &&
             aptDate.getMonth() === date.getMonth() &&
             aptDate.getDate() === date.getDate();
    });
  };

  const getAppointmentCount = (date) => {
    if (!appointments || appointments.length === 0) return 0;
    
    return appointments.filter(apt => {
      if (!apt.dataAgendamento) return false;
      
      let aptDate;
      if (apt.dataAgendamento instanceof Date) {
        aptDate = apt.dataAgendamento;
      } else if (apt.dataAgendamento.toDate) {
        aptDate = apt.dataAgendamento.toDate();
      } else {
        aptDate = new Date(apt.dataAgendamento);
      }
      
      // Comparar apenas dia, mês e ano (ignorar hora)
      return aptDate.getFullYear() === date.getFullYear() &&
             aptDate.getMonth() === date.getMonth() &&
             aptDate.getDate() === date.getDate();
    }).length;
  };

  // Verificar se uma data está selecionada
  const isSelected = (date) => {
    return selectedDate && date.toDateString() === selectedDate.toDateString();
  };

  // Verificar se uma data é hoje
  const isToday = (date) => {
    return date.toDateString() === new Date().toDateString();
  };

  // Formatar o intervalo de datas para exibição
  const getDateRange = () => {
    if (displayedWeeks.length === 0) return '';
    
    const firstDate = displayedWeeks[0][0];
    const lastDate = displayedWeeks[2][6];
    
    const firstDay = firstDate.getDate();
    const firstMonth = firstDate.toLocaleDateString('pt-BR', { month: 'short' });
    const lastDay = lastDate.getDate();
    const lastMonth = lastDate.toLocaleDateString('pt-BR', { month: 'short' });
    const year = firstDate.getFullYear();
    
    if (firstMonth === lastMonth) {
      return `${firstDay} - ${lastDay} de ${firstMonth} ${year}`;
    } else {
      return `${firstDay} de ${firstMonth} - ${lastDay} de ${lastMonth} ${year}`;
    }
  };

  // Dias da semana em português
  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  return (
    <div className="bg-card-light dark:bg-card-dark rounded-lg border border-border-light dark:border-border-dark overflow-hidden w-full max-w-full">
      {/* Barra de navegação */}
      <div className="flex items-center justify-between p-4 border-b border-border-light dark:border-border-dark">
        <button
          onClick={goToPreviousWeek}
          className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-background-light dark:bg-background-dark hover:bg-muted-light dark:hover:bg-muted-dark transition-colors text-foreground-light dark:text-foreground-dark"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Anterior</span>
        </button>
        
        <div className="text-center">
          <h3 className="hidden sm:block text-lg font-semibold text-foreground-light dark:text-foreground-dark">
            {getDateRange()}
          </h3>
        </div>
        
        <button
          onClick={goToNextWeek}
          className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-background-light dark:bg-background-dark hover:bg-muted-light dark:hover:bg-muted-dark transition-colors text-foreground-light dark:text-foreground-dark"
        >
          <span className="text-sm font-medium">Próximo</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Cabeçalho dos dias da semana */}
      <div className="grid grid-cols-7 bg-muted-light/50 dark:bg-muted-dark/50 min-w-0">
        {weekDays.map((day) => (
          <div
            key={day}
            className="p-2 sm:p-3 text-center text-xs sm:text-sm font-medium text-muted-light dark:text-muted-dark border-r border-border-light dark:border-border-dark last:border-r-0 min-w-0 overflow-hidden"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Grade do calendário */}
      <div className="grid grid-rows-3 min-w-0">
        {displayedWeeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 min-w-0">
            {week.map((date, dayIndex) => {
              const day = date.getDate();
              const month = date.toLocaleDateString('pt-BR', { month: 'short' });
              const hasApt = hasAppointments(date);
              const appointmentCount = getAppointmentCount(date);
              const selected = isSelected(date);
              const today = isToday(date);
              
              // Hoje usa o destaque “cheio”; o dia selecionado (se não for hoje) usa anel + fundo suave.
              const cellBg = today
                ? 'border-primary-500 bg-primary-500 text-white dark:border-primary-600 dark:bg-primary-600'
                : selected && hasApt
                  ? 'bg-blue-50/90 text-foreground-light ring-1 ring-inset ring-primary-500 dark:bg-blue-900/25 dark:text-foreground-dark dark:ring-primary-500'
                  : selected
                    ? 'bg-primary-50/80 text-foreground-light ring-1 ring-inset ring-primary-500 dark:bg-primary-950/30 dark:text-foreground-dark dark:ring-primary-500'
                    : hasApt
                      ? 'bg-blue-50 text-foreground-light dark:bg-blue-900/20 dark:text-foreground-dark'
                      : 'bg-card-light text-foreground-light dark:bg-card-dark dark:text-foreground-dark';

              return (
                <button
                  key={`${weekIndex}-${dayIndex}`}
                  onClick={() => onDateSelect(date)}
                  className={`
                    relative min-w-0 p-1.5 text-center transition-all duration-200 sm:p-3
                    border-b border-r border-border-light last:border-r-0 dark:border-border-dark
                    ${today ? 'hover:bg-primary-600 dark:hover:bg-primary-700' : 'hover:bg-primary-50 dark:hover:bg-primary-dark/20'}
                    ${cellBg}
                  `}
                >
                  <div className="flex flex-col items-center space-y-0.5 sm:space-y-1">
                    <div className="flex items-center space-x-0.5 sm:space-x-1">
                      <span className="text-xs sm:text-sm font-medium">{day}</span>
                      <span className="text-[10px] sm:text-xs opacity-70 hidden sm:inline">{month}</span>
                    </div>
                    {hasApt && (
                      <div className={`text-[10px] sm:text-xs font-medium ${
                        today
                          ? 'rounded bg-white/20 px-1 text-white'
                          : 'text-blue-600 dark:text-blue-400'
                      }`}>
                        <span>{appointmentCount}</span>
                        <span className="hidden sm:inline"> agendamento{appointmentCount !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Calendar;
